/*
  Shared custom elements for sp- sections.
  Guarded so the file can be loaded from multiple sections safely.
*/

/* Cross-fades direct children marked with [data-sp-fade] every `interval` ms */
if (!customElements.get('sp-crossfade')) {
  customElements.define(
    'sp-crossfade',
    class SpCrossfade extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll(':scope [data-sp-fade]'));
        if (this.items.length < 2) {
          if (this.items[0]) this.items[0].classList.add('is-active');
          return;
        }
        this.index = 0;
        this.items.forEach((el, i) => el.classList.toggle('is-active', i === 0));
        const interval = parseInt(this.dataset.interval, 10) || 4000;
        this.timer = setInterval(() => {
          this.items[this.index].classList.remove('is-active');
          this.index = (this.index + 1) % this.items.length;
          this.items[this.index].classList.add('is-active');
        }, interval);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
    }
  );
}

/* Tabs: buttons [data-sp-tab="i"] toggle panels [data-sp-panel="i"] */
if (!customElements.get('sp-tabs')) {
  customElements.define(
    'sp-tabs',
    class SpTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-sp-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-sp-panel]'));
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab.dataset.spTab));
        });
        if (this.tabs[0]) this.select(this.tabs[0].dataset.spTab);
      }
      select(id) {
        this.tabs.forEach((tab) => {
          const active = tab.dataset.spTab === id;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        this.panels.forEach((panel) => {
          const active = panel.dataset.spPanel === id;
          panel.classList.toggle('is-active', active);
          panel.toggleAttribute('hidden', !active);
        });
      }
    }
  );
}

/* Scroll-snap slider: [data-sp-track] scrolls; [data-sp-prev]/[data-sp-next] buttons; data-autoplay (ms) */
if (!customElements.get('sp-slider')) {
  customElements.define(
    'sp-slider',
    class SpSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-sp-track]');
        if (!this.track) return;
        this.slides = Array.from(this.track.children);
        this.prevBtn = this.querySelector('[data-sp-prev]');
        this.nextBtn = this.querySelector('[data-sp-next]');
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(1));

        const autoplay = parseInt(this.dataset.autoplay, 10) || 0;
        if (autoplay > 0 && this.slides.length > 1) {
          this.startAutoplay(autoplay);
          this.addEventListener('mouseenter', () => clearInterval(this.timer));
          this.addEventListener('mouseleave', () => this.startAutoplay(autoplay));
        }
        this.updateCounter();
        this.track.addEventListener('scroll', () => {
          clearTimeout(this.scrollDebounce);
          this.scrollDebounce = setTimeout(() => this.updateCounter(), 100);
        });
      }
      disconnectedCallback() {
        clearInterval(this.timer);
      }
      startAutoplay(ms) {
        clearInterval(this.timer);
        this.timer = setInterval(() => this.go(1), ms);
      }
      slideStep() {
        if (this.slides.length < 2) return this.track.clientWidth;
        return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
      }
      currentIndex() {
        const step = this.slideStep();
        return step > 0 ? Math.round(this.track.scrollLeft / step) : 0;
      }
      go(dir) {
        const step = this.slideStep();
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        let target = (this.currentIndex() + dir) * step;
        if (target > maxScroll + 4) target = 0;
        if (target < 0) target = maxScroll;
        this.track.scrollTo({ left: Math.min(target, maxScroll), behavior: 'smooth' });
      }
      updateCounter() {
        const counter = this.querySelector('[data-sp-counter]');
        if (!counter) return;
        const index = Math.min(this.currentIndex(), this.slides.length - 1);
        counter.textContent = String(index + 1).padStart(2, '0');
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      }
    }
  );
}
